import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  TextField,
  MenuItem,
  Stack,
  LinearProgress,
} from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import { CircularProgress, Alert } from '@mui/material';
import { REPS_COLORS, oeBand } from '../../utils/constants';
import { useAuth } from '../../../../contexts/AuthContext';
import { useUserOrganizations } from '../../hooks/useOrganization';
import { useSiteComparisons } from '../../hooks/useSiteComparisons';
import OrgSwitcher from '../../components/OrgSwitcher';
import { useDemoRole } from '../../../../contexts/DemoRoleContext';

/**
 * Site Dashboard — Mindmap branch 2, "Site Dashboard"
 * LIVE: site scores from snapshots; department rollup from the last 14 days
 * of survey responses.
 */
function SiteDashboard() {
  const { user } = useAuth();
  const { organizations } = useUserOrganizations(user?.uid);
  const organization = organizations[0] || null;
  const { comparisons, loading } = useSiteComparisons(organization?.id || null);
  const { queryScope } = useDemoRole();
  const [siteId, setSiteId] = useState('');

  // Scoped viewers (site managers, FLS/DH/ITMs) are pinned to their own site.
  const lockedSiteId = queryScope?.siteId || null;

  if (loading) {
    return <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>;
  }
  if (!comparisons || comparisons.siteScores.length === 0) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
          <LocationCityIcon sx={{ color: 'primary.main', fontSize: 32 }} />
          <Typography variant="h4" sx={{ flexGrow: 1 }}>Site Dashboard</Typography>
          <OrgSwitcher />
        </Box>
        <Alert severity="info">
          The site dashboard appears once your organization has site-level survey data.
        </Alert>
      </Box>
    );
  }

  const activeSiteId = lockedSiteId || siteId || comparisons.siteScores[0].siteId;
  const siteScores = comparisons.siteScores.find((s) => s.siteId === activeSiteId) || comparisons.siteScores[0];
  const site = { id: siteScores.siteId, name: siteScores.siteName };
  const deptScores = comparisons.deptScores.filter((d) => d.siteId === siteScores.siteId);

  const chartData = deptScores.map((d) => ({
    name: d.deptName,
    Reliability: d.scores.reliability,
    Efficiency: d.scores.efficiency,
    Productivity: d.scores.productivity,
    Safety: d.scores.safety,
  }));

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <LocationCityIcon sx={{ color: 'primary.main', fontSize: 32 }} />
        <Typography variant="h4" sx={{ flexGrow: 1 }}>Site Dashboard</Typography>
        <TextField
          select
          size="small"
          label="Site"
          value={siteScores.siteId}
          onChange={(e) => setSiteId(e.target.value)}
          sx={{ minWidth: 240 }}
          disabled={Boolean(lockedSiteId)}
          helperText={lockedSiteId ? 'Scoped to your site' : undefined}
        >
          {(lockedSiteId
            ? comparisons.siteScores.filter((s) => s.siteId === lockedSiteId)
            : comparisons.siteScores
          ).map((s) => (
            <MenuItem key={s.siteId} value={s.siteId}>{s.siteName}</MenuItem>
          ))}
        </TextField>
        <OrgSwitcher />
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Rollup of all departments at <strong>{site?.name}</strong>. Use this view to spot which departments are pulling site averages up or down. Department scores use the last 14 days of responses.
      </Typography>

      <Grid container spacing={3}>
        {/* Summary cards */}
        {siteScores && Object.entries(siteScores.scores).map(([k, v]) => {
          const repsColor = REPS_COLORS[k] || '#666';
          const band = oeBand(v, organization?.thresholds, k);
          return (
            <Grid item xs={6} md={3} key={k}>
              <Card sx={{ borderTop: 4, borderTopColor: repsColor }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ textTransform: 'capitalize', color: repsColor, fontWeight: 700, lineHeight: 1.2 }}>{k}</Typography>
                  <Typography variant="h3" fontWeight={700} sx={{ color: band.color }}>{v.toFixed(1)}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">Site avg</Typography>
                    <Typography variant="caption" sx={{ color: band.color, fontWeight: 700 }}>{band.label}</Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={v * 10}
                    sx={{
                      height: 6, borderRadius: 3, mt: 0.5,
                      backgroundColor: 'rgba(0,0,0,0.08)',
                      '& .MuiLinearProgress-bar': { backgroundColor: band.color },
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>
          );
        })}

        {/* Site overview chart */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>REPS Scores by Department</Typography>
              <Box sx={{ height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis domain={[0, 10]} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Reliability" fill={REPS_COLORS.reliability} />
                    <Bar dataKey="Efficiency" fill={REPS_COLORS.efficiency} />
                    <Bar dataKey="Productivity" fill={REPS_COLORS.productivity} />
                    <Bar dataKey="Safety" fill={REPS_COLORS.safety} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Department list */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Departments</Typography>
              <Stack spacing={1.5}>
                {deptScores.map((ds) => {
                  const teamCount = comparisons.teamScores.filter((t) => t.departmentId === ds.deptId).length;
                  return (
                    <Box key={ds.deptId} sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="body2" fontWeight={600}>{ds.deptName}</Typography>
                        <Chip label={ds.basis || `${ds.responseCount} responses`} size="small" variant="outlined" color={ds.lowConfidence ? 'warning' : 'default'} title={ds.lowConfidence ? 'Low confidence: fewer than 5 responses or under 40 % of the people in this department responded' : undefined} />
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Avg: <strong>{((ds.scores.reliability + ds.scores.efficiency + ds.scores.productivity + ds.scores.safety) / 4).toFixed(1)}</strong>
                        {teamCount > 0 ? <> • {teamCount} team{teamCount !== 1 ? 's' : ''} reporting</> : null}
                      </Typography>
                    </Box>
                  );
                })}
                {deptScores.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No department-level responses in the last 14 days.
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default SiteDashboard;
